/* eslint-disable react/no-multi-comp */
import { useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import classNames from 'classnames'
import { isObject, isString } from 'lodash-es'

import type { OfferPaymentSecondStepFormProps } from 'Modules/purchase/components/OfferPaymentForm/OfferPaymentSecondStepForm.types'
import { StripeInvoiceDetails } from 'Modules/purchase/components/StripeInvoiceDetails/StripeInvoiceDetails'
import type { ClientSecretResponse } from 'Modules/purchase/pages/PurchaseDevice/PurchaseDevice.types'

import { Alert, Heading, PrimaryButton, Text } from 'Components/_theme'
import { LoadingState } from 'Components/LoadingState/LoadingState'

import { fetchFromPublicApiUsingPartialPath } from 'Portal/src/utilities'

export const PaymentFormContent = (props: OfferPaymentSecondStepFormProps) => {
  const { invoiceData, setInvoiceData, uuid } = props

  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false)

  const stripe = useStripe()
  const elements = useElements()
  const intl = useIntl()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [stripeError, setStripeError] = useState<string | undefined>()
  const [isCancelingInvoice, setIsCancelingInvoice] = useState(false)

  const { origin } = new URL(window.location.href)
  const fallbackErrorMessage = intl.formatMessage({
    id: 'ErrorSubmittingRequest',
  })

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    if (!stripe || !elements || !invoiceData?.client_secret) {
      return
    }

    setStripeError(undefined)

    try {
      setIsSubmitting(true)

      // Validate form elements
      const elementsSubmit = await elements.submit()

      if (elementsSubmit?.error) {
        throw new Error(elementsSubmit.error.message)
      }

      // Process payment
      const result = await stripe.confirmPayment({
        clientSecret: invoiceData.client_secret,
        confirmParams: {
          return_url: `${origin}/portal/app/#/purchase-successful`,
        },
        elements,
      })

      if (result?.error) {
        setStripeError(
          isObject(result?.error) &&
            'message' in result.error &&
            isString(result.error.message)
            ? result.error.message
            : fallbackErrorMessage,
        )
      }
    } catch (error) {
      setStripeError(
        isObject(error) && 'message' in error && isString(error.message)
          ? error.message
          : fallbackErrorMessage,
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleGoBack = useCallback(async () => {
    try {
      setIsCancelingInvoice(true)

      const response =
        await fetchFromPublicApiUsingPartialPath<ClientSecretResponse>(
          `/cancel-invoice/${uuid}`,
        )

      if (response.error || !response.invoice_data) {
        throw new Error('There was an error creating your payment intent.')
      }

      setInvoiceData(response.invoice_data)
    } catch (error) {
      setStripeError(
        isObject(error) && 'message' in error && isString(error.message)
          ? error.message
          : fallbackErrorMessage,
      )
    } finally {
      setIsCancelingInvoice(false)
    }
  }, [fallbackErrorMessage, setInvoiceData, uuid])

  return (
    <>
      {!isPaymentElementReady && (
        <div className="d-flex align-items-center justify-content-center flex-fill flex-column">
          <LoadingState />
        </div>
      )}

      <div
        className={classNames('OfferPaymentForm d-flex flex-fill flex-column', {
          'd-none': !isPaymentElementReady,
        })}
      >
        <div className="mb-9">
          <StripeInvoiceDetails invoiceData={invoiceData} />
        </div>

        <form>
          <Heading as={5} className="mb-4" size={5}>
            <FormattedMessage id="OfferPaymentForm_payment_details" />
          </Heading>

          <PaymentElement
            className="mb-9"
            onReady={() => {
              setIsPaymentElementReady(true)
            }}
            options={{
              layout: {
                defaultCollapsed: false,
                type: 'tabs',
              },
              paymentMethodOrder: ['card'],
            }}
          />

          {stripeError && (
            <Alert className="mb-2" variant="warning">
              {stripeError}
            </Alert>
          )}

          <Text className="reviewRequestNotice mb-0">
            <FormattedMessage id="OfferPaymentForm_review_request_objection_text" />
          </Text>

          <div className="d-flex gap-6">
            <PrimaryButton
              appearance="outline"
              className="mt-4 mb-4 w-100"
              isLoading={isSubmitting || isCancelingInvoice}
              onClick={handleGoBack}
            >
              <FormattedMessage id="Back" />
            </PrimaryButton>

            <PrimaryButton
              className="mt-4 mb-4 w-100"
              isLoading={isSubmitting || isCancelingInvoice}
              onClick={handleSubmit}
            >
              <FormattedMessage id="OfferPaymentForm_submit" />
            </PrimaryButton>
          </div>
        </form>
      </div>
    </>
  )
}

/**
 * Second step of the payment form wrapped with Stripe Elements
 */
export const OfferPaymentSecondStepForm = (
  props: OfferPaymentSecondStepFormProps,
) => {
  const stripePromise = loadStripe(window.STRIPE_PUBLISHABLE_KEY)
  const { invoiceData } = props

  return (
    <Elements
      options={{
        clientSecret: invoiceData?.client_secret,
      }}
      stripe={stripePromise}
    >
      <PaymentFormContent {...props} />
    </Elements>
  )
}
